import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useSiteImage } from '@/hooks/useSiteImage';
import { IMAGES } from '@/constants/images';

export default function HeroSection() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const productImageUrl = useSiteImage('home.hero.productImage', IMAGES.threeStepSet);

  return (
    <section
      id="hero"
      className="relative w-full min-h-[100dvh] bg-[#1A1A1A] flex flex-col overflow-hidden"
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] via-[#1A1A1A] to-[#1B3A5C]/20 pointer-events-none" />

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center justify-center px-6 md:px-12 lg:px-20 pt-24 pb-10 gap-10 lg:gap-16 max-w-7xl mx-auto w-full">
        {/* Left side */}
        <div className="flex-1 flex flex-col gap-6 max-w-xl w-full">
          {/* Step badges */}
          <div className="flex items-center gap-3 mb-2 flex-wrap animate-fade-up">
            <div className="flex items-center gap-2">
              {['01', '02', '03'].map((step) => (
                <span
                  key={step}
                  className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-[10px] font-medium text-white/60"
                >
                  {step}
                </span>
              ))}
            </div>
            <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-light">
              {t('home.hero.dailyTreatment')}
            </span>
          </div>

          {/* Main headline */}
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] animate-fade-up-delay-1 break-keep">
            {t('home.hero.prescription')}
          </h1>
          <h2 className="font-serif text-xl sm:text-2xl md:text-4xl lg:text-5xl font-light italic text-white/50 leading-[1.15] -mt-2 animate-fade-up-delay-2 break-keep">
            {(() => {
              const text = t('home.hero.kBeauty');
              const idx = text.indexOf('K-Beauty');
              if (idx === -1) return text;
              return (
                <>
                  {text.slice(0, idx)}
                  <span className="whitespace-nowrap">K-Beauty</span>
                  {text.slice(idx + 8)}
                </>
              );
            })()}
          </h2>

          {/* Body text */}
          <p className="text-sm text-white/50 font-light leading-[1.8] max-w-md animate-fade-up-delay-3 break-keep">
            {t('home.hero.subtitle')}
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-2 animate-fade-up-delay-3">
            <Link
              to={prefix + '/products'}
              className="w-full sm:w-auto sm:flex-shrink-0 px-5 py-3.5 bg-[#7B1113] text-white text-[11px] tracking-[0.12em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center whitespace-pre-line sm:whitespace-pre leading-snug break-words"
            >
              {t('home.hero.ctaShopSet', 'SHOP THE 3-STEP SET — ৳8,000\n(SAVE ৳500)')}
            </Link>
            <Link
              to={prefix + '/stores'}
              className="w-full sm:w-auto px-5 py-3 border border-white/30 text-white text-[11px] tracking-[0.12em] uppercase rounded-full hover:bg-white/10 transition-colors cursor-pointer text-center whitespace-nowrap"
            >
              {t('home.hero.ctaWhereToBuy')}
            </Link>
          </div>
        </div>

        {/* Right side */}
        <div className="flex-1 flex flex-col gap-8 max-w-md w-full animate-fade-up-delay-2">
          {/* 3 step pills */}
          <div className="flex flex-col gap-3">
            {[
              { step: '01', label: t('threeStep.step01.badge'), name: t('home.hero.product01') },
              { step: '02', label: t('threeStep.step02.badge'), name: t('home.hero.product02') },
              { step: '03', label: t('threeStep.step03.badge'), name: t('home.hero.product03') },
            ].map((item) => (
              <div
                key={item.step}
                className="flex items-center gap-4 px-5 py-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
              >
                <span className="text-[10px] font-bold text-[#7B1113] tracking-wider flex-shrink-0">
                  {item.step}
                </span>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[10px] tracking-[0.15em] uppercase text-white/40 font-medium">
                    {item.label}
                  </span>
                  <span className="text-sm text-white font-light truncate">{item.name}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-white/60 font-medium">{t('home.hero.statClean')}</span>
              <span className="text-[10px] text-white/30 font-light">{t('home.hero.statCleanSub')}</span>
            </div>
            <div className="w-px h-8 bg-white/10 hidden sm:block" />
            <div className="flex flex-col gap-1">
              <span className="text-xs text-white/60 font-medium">
                K<span className="text-[#7B1113]">+</span> Derma
              </span>
              <span className="text-[10px] text-white/30 font-light">{t('home.hero.statDermaSub')}</span>
            </div>
            <div className="w-px h-8 bg-white/10 hidden sm:block" />
            <div className="flex flex-col gap-1">
              <span className="text-xs text-white/60 font-medium">
                <span className="text-[#7B1113]">Rx</span> Prescribed
              </span>
              <span className="text-[10px] text-white/30 font-light">{t('home.hero.statPrescribedSub')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="relative z-10 w-full flex justify-center px-6 md:px-12 lg:px-20 pb-8">
        <div className="max-w-7xl w-full grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: 'ri-shield-check-line', label: t('home.hero.trustBadge1') },
            { icon: 'ri-flask-line', label: t('home.hero.trustBadge2') },
            { icon: 'ri-file-list-3-line', label: t('home.hero.trustBadge3') },
            { icon: 'ri-map-pin-line', label: t('home.hero.trustBadge4') },
          ].map((badge) => (
            <div
              key={badge.label}
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-white/5 border border-white/10"
            >
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className={`${badge.icon} text-white/40 text-sm`}></i>
              </div>
              <span className="text-[11px] text-white/60 font-light leading-tight">
                {badge.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom product group image */}
      <div className="relative z-10 w-full flex justify-center pb-6 px-6 animate-fade-up-delay-3">
        <div className="max-w-lg w-full">
          <img
            src={productImageUrl}
            alt="Rx.K Seoul 3-Step Daily Treatment Product Set"
            className="w-full h-auto object-contain opacity-90"
          />
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-[10px] tracking-[0.2em] uppercase text-white/30 font-light">
          {t('home.hero.scroll')}
        </span>
        <div className="w-px h-6 bg-white/20 animate-pulse" />
      </div>
    </section>
  );
}